// Team-event feed helpers — system rows that render alongside team_messages
// inside the team chat ("Test Student joined", "X was demoted by Y", etc.).
//
// Backed by the team_events table. The "kind" enum mirrors the DB CHECK:
//   joined | left | removed | promoted_captain | demoted_captain
//
// Each row carries actor_id and target_id. The render layer resolves names
// via user_profiles and decides which copy template to use based on whether
// actor_id == target_id (self-action) vs different (other-action).

import type { RealtimeChannel } from "@supabase/supabase-js";

import { supabase } from "./supabase";

export type TeamEventKind =
  | "joined"
  | "left"
  | "removed"
  | "promoted_captain"
  | "demoted_captain";

export type TeamEvent = {
  id: string;
  team_id: string;
  kind: TeamEventKind;
  actor_id: string | null;
  target_id: string;
  created_at: string;
  /** Display names resolved from user_profiles. Null if the user no longer exists. */
  actor_name: string | null;
  target_name: string | null;
};

type RawTeamEvent = {
  id: string;
  team_id: string;
  kind: TeamEventKind;
  actor_id: string | null;
  target_id: string;
  created_at: string;
};

/** Most recent N team_events for a team, oldest-first to match team_messages. */
export async function listTeamEvents(teamId: string, limit = 200): Promise<TeamEvent[]> {
  const { data, error } = await supabase
    .from("team_events")
    .select("id, team_id, kind, actor_id, target_id, created_at")
    .eq("team_id", teamId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error || !data) return [];
  const rows = (data as RawTeamEvent[]).reverse();
  return enrichWithNames(rows);
}

/** Single fetch of one event by id — used after a realtime INSERT to enrich the row. */
async function fetchTeamEventById(id: string): Promise<TeamEvent | null> {
  const { data, error } = await supabase
    .from("team_events")
    .select("id, team_id, kind, actor_id, target_id, created_at")
    .eq("id", id)
    .maybeSingle();
  if (error || !data) return null;
  const enriched = await enrichWithNames([data as RawTeamEvent]);
  return enriched[0] ?? null;
}

async function enrichWithNames(rows: RawTeamEvent[]): Promise<TeamEvent[]> {
  if (rows.length === 0) return [];
  const ids = new Set<string>();
  for (const r of rows) {
    if (r.actor_id) ids.add(r.actor_id);
    ids.add(r.target_id);
  }
  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, full_name")
    .in("id", Array.from(ids));
  const nameById = new Map<string, string>();
  for (const p of (profiles ?? []) as Array<{ id: string; full_name: string | null }>) {
    if (p.full_name) nameById.set(p.id, p.full_name);
  }
  return rows.map((r) => ({
    ...r,
    actor_name: r.actor_id ? nameById.get(r.actor_id) ?? null : null,
    target_name: nameById.get(r.target_id) ?? null,
  }));
}

/**
 * Realtime subscription — calls onInsert with a fully-enriched TeamEvent each
 * time a new row lands for this team. Returns a cleanup fn.
 */
export function subscribeToTeamEvents(
  teamId: string,
  onInsert: (event: TeamEvent) => void
): () => void {
  const channel: RealtimeChannel = supabase
    .channel(`team-events:${teamId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "team_events",
        filter: `team_id=eq.${teamId}`,
      },
      (payload) => {
        const id = (payload.new as { id?: string } | null)?.id;
        if (!id) return;
        void fetchTeamEventById(id).then((evt) => {
          if (evt) onInsert(evt);
        });
      }
    )
    .subscribe();
  return () => {
    try {
      void supabase.removeChannel(channel);
    } catch {
      // already removed
    }
  };
}
